import { createFileRoute, Link } from '@tanstack/react-router';
import { Fragment, useMemo } from 'react';
import { useUncashedChecks } from '../../hooks/usePayroll';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { formatAccountingDate, formatCurrency } from '../../lib/formatters';

export const Route = createFileRoute('/_auth/payroll/uncashed')({
  component: UncashedChecksPage,
});

function UncashedChecksPage() {
  const companyId = useActiveCompanyId();
  const { data: checks = [], isLoading, error } = useUncashedChecks(companyId);

  // Group by person: the question this page answers is "whose money hasn't
  // left the bank", so the person is the row and their cheques are the detail.
  const byEmployee = useMemo(() => {
    const m = new Map<string, typeof checks>();
    for (const c of checks) {
      const key = c.employeeName ?? 'Not from the payroll module';
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(c);
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [checks]);

  const total = useMemo(
    () => checks.reduce((s, c) => s + parseFloat(c.outstanding), 0),
    [checks],
  );

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <h1 style={{ marginTop: 0 }}>Uncashed paycheques</h1>
        <p className="muted">No active company.</p>
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div>
          <h1 style={{ fontSize: 24, margin: 0 }}>Uncashed paycheques</h1>
          <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>
            Net pay still sitting in the holding account — the money left the payroll
            register but not yet the bank.
          </p>
        </div>
        <Link to="/payroll" className="btn-secondary" style={{ textDecoration: 'none' }}>
          Back to payroll
        </Link>
      </div>

      {isLoading && <p className="muted">Loading…</p>}
      {error && <p className="field-error">Failed to load uncashed paycheques.</p>}

      {!isLoading && !error && checks.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: 32 }}>
          <p className="muted" style={{ margin: 0 }}>
            Nothing outstanding — every paycheque has been settled by a bank draft.
          </p>
        </div>
      )}

      {checks.length > 0 && (
        <>
          <div className="card" style={{ marginBottom: 16 }}>
            <strong>{formatCurrency(total.toFixed(2))}</strong> outstanding across{' '}
            {checks.length} paycheque{checks.length === 1 ? '' : 's'} and {byEmployee.length}{' '}
            {byEmployee.length === 1 ? 'person' : 'people'}.
          </div>

          <table className="table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Pay date</th>
                <th>Entry</th>
                <th className="right" style={{ width: 130 }}>
                  Net pay
                </th>
                <th className="right" style={{ width: 130 }}>
                  Settled
                </th>
                <th className="right" style={{ width: 140 }}>
                  Outstanding
                </th>
                <th style={{ width: 1 }}></th>
              </tr>
            </thead>
            <tbody>
              {byEmployee.map(([name, rows]) => {
                const subtotal = rows.reduce((s, c) => s + parseFloat(c.outstanding), 0);
                return (
                  <Fragment key={name}>
                    {rows.map((c, i) => (
                      <tr key={c.journalLineId}>
                        <td>{i === 0 ? <strong>{name}</strong> : ''}</td>
                        <td>{formatAccountingDate(c.payDate)}</td>
                        <td>
                          <Link to="/journal/$id" params={{ id: c.journalEntryId }}>
                            {c.entryNumber}
                          </Link>
                        </td>
                        <td className="right">{formatCurrency(c.amount)}</td>
                        <td className="right">
                          {parseFloat(c.settled) > 0 ? (
                            formatCurrency(c.settled)
                          ) : (
                            <span className="muted">—</span>
                          )}
                        </td>
                        <td className="right" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {formatCurrency(c.outstanding)}
                        </td>
                        <td>
                          <Link
                            to="/checks/new"
                            search={{ payLine: c.journalLineId }}
                            className="btn-secondary"
                            style={{ textDecoration: 'none', whiteSpace: 'nowrap' }}
                            title="Write or print the cheque for this net pay. It debits the holding account, not an expense."
                          >
                            Write check
                          </Link>
                        </td>
                      </tr>
                    ))}
                    {rows.length > 1 && (
                      <tr>
                        <td colSpan={5} className="right muted">
                          {name} total
                        </td>
                        <td className="right">
                          <strong>{formatCurrency(subtotal.toFixed(2))}</strong>
                        </td>
                        <td></td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>

          <p className="muted" style={{ fontSize: 12, marginTop: 12 }}>
            A cheque clears this list when a bank draft settles it line by line, or when you
            write the cheque from here — that check pays the holding account, not an expense. If a draft
            was posted as one lump debit, every paycheque it paid stays listed — a lump
            debit carries no record of whose cheque it cleared.
          </p>
        </>
      )}
    </div>
  );
}
